import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import ControlledToggleButton from './components/ControlledToggleButton';

import type { IToggleProvider, IToggleContext, IToggleProps } from './shared/types';

const ToggleContext = createContext({} as IToggleContext);

export function ToggleState({ initialValue = false, onChange, children }: IToggleProvider) {
    const [isActive, setIsActive] = useState(initialValue);

    const switchOn = useCallback(() => {
        setIsActive(true);
        onChange(true);
    }, [onChange]);

    const switchOff = useCallback(() => {
        setIsActive(false);
        onChange(false);
    }, [onChange]);

    const toggle = useCallback(() => {
        if (!isActive) return switchOn();
        return switchOff();
    }, [isActive, switchOff, switchOn]);

    const value = useMemo(
        () => ({
            isActive,
            switchOn,
            switchOff,
            toggle,
            onChange
        }),
        [isActive, onChange, switchOff, switchOn, toggle]
    );

    return <ToggleContext.Provider value={value}>{children}</ToggleContext.Provider>;
}

export function useToggle() {
    return useContext(ToggleContext);
}

function ToggleOn({ children }) {
    const { isActive } = useToggle();

    return isActive ? children : null;
}

function ToggleOff({ children }) {
    const { isActive } = useToggle();

    return isActive ? null : children;
}

function ToggleButton(props: IToggleProps) {
    const { toggle, isActive } = useToggle();

    return <ControlledToggleButton {...props} onChange={toggle} active={isActive} />;
}

function WithToggleContext({ children }) {
    const toggleContext = useToggle();

    return children(toggleContext);
}

const Toggle = Object.assign(ToggleState, {
    Button: ToggleButton,
    On: ToggleOn,
    Off: ToggleOff,
    WithContext: WithToggleContext
});

export default Toggle;
